import { connect } from "react-redux";
import { RootState } from "state/rootReducer";
import { formatDuration } from "shared/formatDuration";
import "./SongListItem.css";
import { ICON_STROKE, PlayIcon, RemoveIcon } from "shared/icons";

interface StateProps {
  token: string | null;
}

interface OwnProps {
  song: Song;
  otherdata?: string | JSX.Element;
  otherdataVariant?: "text" | "controls";
  index: number;
  selectSongHandler: (song: Song) => void;
  deleteSongHandler?: (song: Song) => void;
  overridePlayHandler?: (song: Song, selected: boolean) => void;
  addStartingAtSong: (song: Song) => void;
  currentlyPlaying: boolean;
  currentlySelected?: boolean;
}

export type SongListItemProps = StateProps & OwnProps;

/**
 * A stand-in for album art.
 *
 * Real artwork is only fetched for the *selected* song (songsSlice's
 * fetchArtUri makes one Spotify call per track), so the list has no art to
 * show. Deriving a stable colour from the track's gid gives each row the same
 * visual anchor artwork would, without N API calls per page.
 */
function swatchStyle(gid: string): React.CSSProperties {
  let hash = 0;
  for (let i = 0; i < gid.length; i++) {
    hash = (hash * 31 + gid.charCodeAt(i)) % 360;
  }
  return {
    background: `linear-gradient(135deg, hsl(${hash} 55% 42%), hsl(${
      (hash + 40) % 360
    } 45% 22%))`,
  };
}

export function SongListItem(props: SongListItemProps): JSX.Element {
  function handleAddToPlaylistClick(evt: React.MouseEvent): void {
    evt.stopPropagation();

    props.addStartingAtSong(props.song);
  }

  function handleDelete(evt: React.MouseEvent): void {
    evt.stopPropagation();

    props.deleteSongHandler!(props.song);
  }

  function render(): JSX.Element {
    const evenOdds = props.index % 2 === 0 ? "row-even" : "row-odd";
    const controls = props.otherdataVariant === "controls";
    const gridClass = !props.otherdata
      ? "song-grid"
      : controls
        ? "song-grid song-grid--controls"
        : "song-grid song-grid--extra";

    return (
      <div className={props.currentlyPlaying ? "selected-song" : ""}>
        <div
          className={`${gridClass} ${evenOdds} px-2 py-1.5 cursor-pointer`}
          onClick={() => props.selectSongHandler(props.song)}
        >
          {/* Track: artwork stand-in, then title over artist. Stacking them
              halves the row's horizontal demand and gives a clear anchor. */}
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-9 h-9 rounded flex-none"
              style={swatchStyle(props.song.gid)}
              aria-hidden="true"
            ></div>
            <div className="min-w-0">
              <div className="song-title text-sm text-ink truncate">
                {props.song.name}
              </div>
              <div className="text-xs text-muted truncate">
                {props.song.artists.map((artist) => artist.name).join(", ")}
              </div>
            </div>
          </div>

          {/* Controls skip `truncate`: its overflow clip would cut off their
              focus rings. */}
          {props.otherdata && (
            <div
              className={
                controls
                  ? "song-extra flex items-center gap-1.5"
                  : "song-extra text-xs text-ink-2 truncate"
              }
            >
              {props.otherdata}
            </div>
          )}

          <div className="song-time text-xs text-ink-2 tabular-nums text-right">
            {formatDuration(props.song.duration)}
          </div>

          <div className="flex items-center justify-end gap-1">
            {props.deleteSongHandler && (
              <button
                type="button"
                className="button-small px-1.5 py-1"
                onClick={handleDelete}
                aria-label="Remove"
                title="Remove"
              >
                <RemoveIcon
                  size={15}
                  strokeWidth={ICON_STROKE}
                  aria-hidden="true"
                />
              </button>
            )}
            {!props.overridePlayHandler && props.song.spotify_id && (
              <button
                type="button"
                className="button-small px-1.5 py-1"
                onClick={(evt) => handleAddToPlaylistClick(evt)}
                aria-label="Play from here"
                title="Play from here"
              >
                <PlayIcon
                  size={15}
                  strokeWidth={ICON_STROKE}
                  aria-hidden="true"
                />
              </button>
            )}
            {props.overridePlayHandler && (
              <input
                className="bg-surface accent-accent"
                type="checkbox"
                checked={props.currentlySelected}
                aria-label={`Select ${props.song.name}`}
                onClick={(evt) => evt.stopPropagation()}
                onChange={(evt) =>
                  props.overridePlayHandler!(
                    props.song,
                    (evt.target as HTMLInputElement).checked
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return render();
}

function mapState(state: RootState): StateProps {
  return {
    token: state.user.accessToken,
  };
}

export default connect(mapState)(SongListItem);
